import React, { useState, useEffect } from 'react';
import { Transaction } from '../types';
import { FunctionDeclaration, Type } from '@google/genai';
import { geminiProxy } from '../services/geminiProxy';

interface TransactionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (transaction: Omit<Transaction, 'id' | 'balance'>, id?: string) => void;
    categories: string[];
    transactionToEdit?: Transaction | null;
    ai: boolean;
    currency: string;
    textForAiProcessing: string | null;
    onAiProcessingComplete: () => void;
}

const TransactionModal: React.FC<TransactionModalProps> = ({ isOpen, onClose, onSave, categories, transactionToEdit, ai, currency, textForAiProcessing, onAiProcessingComplete }) => {
    const [type, setType] = useState<'income' | 'expense'>('expense');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [concept, setConcept] = useState('');
    const [counterparty, setCounterparty] = useState('');
    const [amount, setAmount] = useState('');
    const [category, setCategory] = useState<string[]>([]);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [modalCategories, setModalCategories] = useState<string[]>(categories);
    const [isAiProcessing, setIsAiProcessing] = useState(false);
    const [aiSuggestedCategory, setAiSuggestedCategory] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            const sortedCategories = [...categories].sort();
            setModalCategories(sortedCategories);

            if (transactionToEdit) {
                setType(transactionToEdit.amount >= 0 ? 'income' : 'expense');
                setDate(transactionToEdit.date);
                setConcept(transactionToEdit.concept);
                setCounterparty(transactionToEdit.counterparty && transactionToEdit.counterparty !== 'N/A' ? transactionToEdit.counterparty : '');
                setAmount(String(Math.abs(transactionToEdit.amount)));
                setCategory(transactionToEdit.category ? [...transactionToEdit.category] : []);
                setPaymentMethod(transactionToEdit.paymentMethod || '');
            } else {
                // Reset form for new entry, but don't overwrite if AI is about to process
                if (!textForAiProcessing) {
                    setType('expense');
                    setDate(new Date().toISOString().split('T')[0]);
                    setConcept('');
                    setCounterparty('');
                    setAmount('');
                    setCategory([]);
                    setPaymentMethod('');
                }
            }
        }
    }, [transactionToEdit, isOpen, categories]);


    const processTextWithAI = async (text: string) => {
        if (!ai) return;

        setIsAiProcessing(true);
        setAiSuggestedCategory(null);
        
        try {
            const today = new Date().toISOString().split('T')[0];
            const availableCategories = categories.filter(c => c !== "Desconocida").join(', ');

            const registerTransactionFunction: FunctionDeclaration = {
                name: 'registerTransaction',
                description: `Registra una transacción financiera a partir del texto del usuario. La fecha de hoy es ${today}.`,
                parameters: {
                    type: Type.OBJECT,
                    properties: {
                        transactionType: { type: Type.STRING, description: 'Tipo: "ingreso" o "gasto".' },
                        amount: { type: Type.NUMBER, description: 'La cantidad numérica del movimiento.' },
                        concept: { type: Type.STRING, description: 'Descripción de la transacción.' },
                        counterparty: { type: Type.STRING, description: 'Opcional. La tienda o persona involucrada.' },
                        category: { 
                            type: Type.STRING, 
                            description: `Una categoría de esta lista: [${availableCategories}]. Si ninguna encaja, usa "Desconocida".`
                        },
                        suggestedCategory: {
                             type: Type.STRING,
                             description: 'Opcional. Si la categoría es "Desconocida", sugiere un nombre para una nueva categoría (ej. "Mascotas").'
                        },
                        paymentMethod: { type: Type.STRING, description: 'Método de pago, como "Tarjeta" o "Efectivo".' },
                        date: { type: Type.STRING, description: 'Opcional. Fecha en formato AAAA-MM-DD.' },
                    },
                    required: ['transactionType', 'amount', 'concept', 'category'],
                },
            };

            const response = await geminiProxy.generateContent(
                `Analiza el siguiente texto y extrae la información de la transacción: "${text}"`,
                {
                    tools: [{ functionDeclarations: [registerTransactionFunction] }],
                },
            );
            
            const functionCall = response.functionCalls?.[0];

            if (functionCall && functionCall.name === 'registerTransaction' && functionCall.args) {
                const args = functionCall.args;
                const transactionType = (args.transactionType as string)?.toLowerCase();
                setType(transactionType === 'ingreso' || transactionType === 'income' ? 'income' : 'expense');
                setAmount(String(args.amount as number || ''));
                setConcept(args.concept as string || '');
                setCounterparty(args.counterparty as string || '');
                
                const receivedCategory = (args.category as string)?.trim();
                if (receivedCategory) setCategory([receivedCategory]);

                if (args.suggestedCategory) setAiSuggestedCategory((args.suggestedCategory as string).trim());
                
                const pm = (args.paymentMethod as string)?.toLowerCase() || '';
                const matchedMethod = ["Efectivo", "Tarjeta", "Transferencia", "Bizum"].find(m => pm.includes(m.toLowerCase()));
                setPaymentMethod(matchedMethod || 'Otro');
                
                if (args.date && /^\d{4}-\d{2}-\d{2}$/.test(args.date as string)) {
                    setDate(args.date as string);
                } else {
                    setDate(new Date().toISOString().split('T')[0]);
                }
            } else {
                // Fallback if function calling fails
                setConcept(text);
            }

        } catch (error) {
            console.error("Error processing text with AI:", error);
            // If AI fails, just put the raw text in the concept field for manual entry
            setConcept(text);
        } finally {
            setIsAiProcessing(false);
            onAiProcessingComplete();
        }
    };

    // Effect to trigger AI processing when text is received
    useEffect(() => {
      if (isOpen && textForAiProcessing && ai) {
        processTextWithAI(textForAiProcessing);
      }
    }, [isOpen, textForAiProcessing, ai]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const numericAmount = parseFloat(amount);
        if (!concept || isNaN(numericAmount) || numericAmount <= 0 || category.length === 0 || !category[0]) {
            alert('Por favor, rellena todos los campos obligatorios: concepto, importe y categoría.');
            return;
        }

        onSave({
            date,
            concept,
            amount: type === 'income' ? numericAmount : -numericAmount,
            counterparty: counterparty.trim() || 'N/A',
            category,
            paymentMethod: paymentMethod || undefined,
        }, transactionToEdit?.id);
        onClose();
    };

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = e.target.value;
        setAiSuggestedCategory(null); // Clear suggestion on manual change
        if (selectedValue === '__CREATE_NEW__') {
            const newCategoryName = prompt('Introduce el nombre de la nueva categoría:');
            if (newCategoryName && newCategoryName.trim() !== '') {
                const trimmedName = newCategoryName.trim();
                if (!modalCategories.includes(trimmedName)) {
                    setModalCategories(prev => [...prev, trimmedName].sort());
                }
                setCategory([trimmedName]);
            }
        } else {
            setCategory([selectedValue]);
        }
    };

    const handleUseSuggestion = () => {
        if (aiSuggestedCategory) {
            if (!modalCategories.includes(aiSuggestedCategory)) {
                setModalCategories(prev => [...prev, aiSuggestedCategory].sort());
            }
            setCategory([aiSuggestedCategory]);
            setAiSuggestedCategory(null);
        }
    };
    
    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center"
            onClick={onClose}
        >
            <div 
                className="bg-light-surface dark:bg-surface rounded-lg shadow-xl p-8 w-[95%] sm:w-full max-w-lg border border-light-border dark:border-border relative"
                onClick={e => e.stopPropagation()}
            >
                {isAiProcessing && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col justify-center items-center z-10 rounded-lg">
                         <div className="w-12 h-12 border-4 border-white rounded-full border-t-transparent animate-spin"></div>
                         <p className="text-white mt-4 font-semibold">La IA está procesando...</p>
                    </div>
                )}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-light-text-primary dark:text-text-primary">
                        {transactionToEdit ? 'Editar Movimiento' : 'Añadir Movimiento'}
                    </h2>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Tipo de movimiento</label>
                        <div className="flex gap-4">
                            <button type="button" onClick={() => setType('expense')} className={`flex-1 py-2 rounded-md transition-colors ${type === 'expense' ? 'bg-red-500 text-white' : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'}`}>Gasto</button>
                            <button type="button" onClick={() => setType('income')} className={`flex-1 py-2 rounded-md transition-colors ${type === 'income' ? 'bg-secondary text-white' : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'}`}>Ingreso</button>
                        </div>
                    </div>
                     <div>
                        <label htmlFor="date" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Fecha</label>
                        <input type="date" id="date" value={date} onChange={e => setDate(e.target.value)} required className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
                    </div>
                     <div>
                        <label htmlFor="concept" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Concepto</label>
                        <input type="text" id="concept" value={concept} onChange={e => setConcept(e.target.value)} placeholder="Ej: Compra supermercado" required className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
                    </div>
                     <div>
                        <label htmlFor="counterparty" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Contrapartida (Opcional)</label>
                        <input type="text" id="counterparty" value={counterparty} onChange={e => setCounterparty(e.target.value)} placeholder="Ej: Mercadona, Netflix" className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
                    </div>
                     <div>
                        <label htmlFor="amount" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Importe ({currency})</label>
                        <input type="number" id="amount" value={amount} onChange={e => setAmount(e.target.value)} step="0.01" min="0" placeholder="50.00" required className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                        <label htmlFor="category" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Categoría</label>
                        <select
                            id="category"
                            value={category[0] || ''}
                            onChange={handleCategoryChange}
                            required
                            className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            <option value="" disabled>{category[0] ? '' : 'Selecciona una categoría'}</option>
                            {modalCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            <option value="" disabled>──────────────────</option>
                            <option value="__CREATE_NEW__">-- Crear nueva categoría --</option>
                        </select>
                         {aiSuggestedCategory && (
                            <div className="mt-2 text-center">
                                <button
                                    type="button"
                                    onClick={handleUseSuggestion}
                                    className="px-3 py-1.5 text-xs text-secondary bg-green-500/10 hover:bg-green-500/20 rounded-full transition-colors"
                                >
                                    Sugerencia: crear una categoria llamada "{aiSuggestedCategory}"
                                </button>
                            </div>
                        )}
                    </div>
                    <div>
                        <label htmlFor="paymentMethod" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Método de Pago</label>
                        <select 
                            id="paymentMethod" 
                            value={paymentMethod} 
                            onChange={e => setPaymentMethod(e.target.value)} 
                            className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            <option value="">No especificado</option>
                            <option value="Efectivo">Efectivo</option>
                            <option value="Tarjeta">Tarjeta</option>
                            <option value="Transferencia">Transferencia</option>
                            <option value="Bizum">Bizum</option>
                            <option value="Recibo Bancario">Recibo Bancario</option>
                            <option value="Otro">Otro</option>
                        </select>
                    </div>
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-light-text-primary dark:text-text-primary transition-colors">Cancelar</button>
                        <button type="submit" className="px-4 py-2 rounded-md bg-primary hover:bg-blue-600 text-white font-semibold transition-colors">
                            {transactionToEdit ? 'Guardar Cambios' : 'Guardar Movimiento'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TransactionModal;